/**
 * Metric selection and prioritization logic
 */

import { DetailItem, ActivityType } from './types';

/**
 * Priority order for metrics based on activity type
 */
const METRIC_PRIORITY: Record<ActivityType, readonly string[]> = {
    steps: [
        'steps',
        'stepcount',
        'distance',
        'duration',
        'time',
        'calories',
        'burned'
    ],
    workout: [
        'workouttype',
        'activitytype',
        'duration',
        'time',
        'distance',
        'pace',
        'calories',
        'burned',
        'maxspeed',
        'averagespeed',
        'speed',
        'elevation',
        'heart',
        'vo2'
    ],
    facility: ['facility', 'visit', 'duration', 'time', 'location']
} as const;

/**
 * Groups to fallback to when priority tokens don't match enough metrics
 */
const FALLBACK_GROUPS = ['Performance', 'Activity Profile'] as const;

/**
 * Selects the most relevant metrics to display based on activity type
 *
 * @param items - Available detail items
 * @param activityType - Type of activity (steps, workout, facility)
 * @param limit - Maximum number of metrics to select
 * @returns Array of selected metrics in priority order
 */
export const selectMetricItems = (
    items: DetailItem[],
    activityType: ActivityType,
    limit: number
): DetailItem[] => {
    const selected: DetailItem[] = [];
    const usedKeys = new Set<string>();
    const tokens = METRIC_PRIORITY[activityType] ?? METRIC_PRIORITY.workout;

    // First pass: select by priority tokens
    tokens.forEach((token) => {
        if (selected.length >= limit) return;
        const match = items.find(
            (item) =>
                !usedKeys.has(item.key) &&
                item.key.toLowerCase().includes(token)
        );
        if (match) {
            selected.push(match);
            usedKeys.add(match.key);
        }
    });

    // Second pass: fill remaining slots from fallback groups
    FALLBACK_GROUPS.forEach((group) => {
        items
            .filter((item) => !usedKeys.has(item.key) && item.group === group)
            .forEach((item) => {
                if (selected.length < limit) {
                    selected.push(item);
                    usedKeys.add(item.key);
                }
            });
    });

    return selected.slice(0, limit);
};
